import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  deleteMatterEmailV2,
  generateMatterEmailDownloadUrlV2,
  type MatterEmailDownloadRequest,
  sendMatterEmailV2,
  type SendMatterEmailPayload,
} from "@/lib/data/requests/matters/matter-email";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIError, APIResponse, Matter } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";
import { parseEmlPreviewContent } from "@/lib/utils/matterEmail";

export const matterEmailPreviewQueryOptions = ({
  emailId,
  attachmentId,
  key,
}: MatterEmailDownloadRequest & { key: string }) =>
  queryOptions({
    enabled: Boolean(emailId && key),
    queryKey: [queryKeys.matterEmailPreview, emailId, attachmentId, key],
    queryFn: async () => {
      const presignedRes = await generateMatterEmailDownloadUrlV2({
        emailId,
        attachmentId,
      });
      const response = await fetch(presignedRes.data.presignedUrl);
      if (!response.ok)
        throw new Error("Unable to retrieve the email preview.");
      return parseEmlPreviewContent(await response.arrayBuffer());
    },
  });

export const useDeleteMatterEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMatterEmailV2,
    onMutate: (data) => {
      const previousData = queryClient.getQueryData<APIResponse<Matter>>(
        matterDetailQueryKey(data.matterId),
      );
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              emails: oldData.data.emails.filter(
                (email) => email.id !== data.emailId,
              ),
              emailAttachments: oldData.data.emailAttachments.filter(
                (attachment) => attachment.emailId !== data.emailId,
              ),
            },
          };
        },
      );
      return {
        previousData,
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Unable to delete matter email");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      toast.success("Matter email deleted");
    },
  });
};

export const useSendMatterEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendMatterEmailPayload) => sendMatterEmailV2(payload),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
      toast.success("Email sent successfully");
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Failed to send email");
    },
  });
};
